import type { Locale } from "@/lib/i18n";

type Messages = {
  common: {
    viewProject: string;
    processTitle: string;
    processBoldTitle: string;
    ctaGetStarted: string;
    loading: string;
  };
  home: {
    metaTitle: string;
    metaDescription: string;
    heroHeadline: string;
    heroSubheadline: string;
    heroDescription: string;
    heroCtaText: string;
    heroCtaText2: string;
  };
  allDone: {
    headline: string;
    subheadline: string;
    services: { title: string; description: string }[];
  };
  homePage: {
    sectionLabels: {
      services: string;
      whatWeDo: string;
      projects: string;
      faq: string;
    };
    hero: {
      ofWorld: string;
      needsUpdate: string;
    };
    movingCardsCta: string;
    /** "Read all reviews" link under the homepage reviews heading → /reviews. */
    reviewsLink: string;
    /** Line under the homepage Clutch badge row. Placeholders: {rating}, {count}, {date}. */
    clutchBadgesNote: string;
    faq: {
      titleLine1: string;
      titleLine2: string;
      subtitle: string;
    };
    serviceFan: Record<string, string>;
    movingCards: Record<
      string,
      { title: string; description: string; tags?: string[] }
    >;
    accordion: Record<string, { title: string; description: string }>;
    featuredProjects: Record<string, { title: string; description: string }>;
    whyChoose: {
      title: string;
      description: string;
      description2: string;
      features: string[];
    };
    benefits: {
      title: string;
      subtitle: string;
      items: Array<{ title: string; description: string }>;
    };
    process: {
      title: string;
      subtitle: string;
      steps: Array<{ title: string; description: string }>;
    };
    industries: {
      title: string;
      subtitle: string;
      items: string[];
    };
  };
  workPage: {
    metaTitle: string;
    metaDescription: string;
    hero: { title: string; boldTitle: string; subtitle: string };
    intro: { title: string; paragraphs: string[] };
    cta: { title: string; subtitle: string; buttonText: string };
    learnMore: string;
    categories: Record<string, string>;
    notFound: {
      metaTitle: string;
      metaDescription: string;
      title: string;
      back: string;
    };
    details: {
      backToAllProjects: string;
      viewLiveProject: string;
      caseStudySuffix: string;
      defaultMetaDescription: string;
      industryLabel: string;
      headquartersLabel: string;
      platformLabel: string;
      challengeTitle: string;
      solutionTitle: string;
      impactTitle: string;
      impact: {
        efficiencyIncrease: string;
        userEngagement: string;
        activeUsers: string;
      };
      galleryItemAlt: string;
      ctaTitle: string;
      ctaDescription: string;
    };
    projects: Record<
      string,
      {
        title: string;
        description: string;
        fullDescription: string;
        challenge: string;
        solution: string;
      }
    >;
  };
  nav: {
    services: string;
    solutions: string;
    work: string;
    insights: string;
    team: string;
    careers: string;
    about: string;
    contact: string;
    inquiryNow: string;
    viewAllServices: string;
    viewAllSolutions: string;
    servicesDropdownHint: string;
    solutionsDropdownHint: string;
    healthcare: string;
    healthcarePanelEyebrow: string;
    healthcarePanelTitle: string;
    healthcarePanelIso: string;
    healthcarePanelBaa: string;
    exploreHealthcare: string;
    seeCaseStudies: string;
    servicesPanelEyebrow: string;
    servicesPanelTitle: string;
    servicesPanelBody: string;
    exploreServices: string;
    company: string;
    companyPanelEyebrow: string;
    companyPanelTitle: string;
    companyPanelBody: string;
    socialsTitle: string;
  };
  pages: {
    services: { our: string; title: string; subtitle: string; learnMore: string };
    solutions: { our: string; title: string; subtitle: string; learnMore: string };
    relatedInsights: { eyebrow: string; title: string; allInsights: string };
    relatedLinks: {
      eyebrow: string;
      title: string;
      specialtiesEyebrow: string;
      specialtiesTitle: string;
      siblingsEyebrow: string;
      overview: string;
    };
  };
  contact: {
    kicker: string;
    title: string;
    bookMeeting: string;
    supportEmail: string;
    callAnytime: string;
    liveChat: string;
    whatsappContact: string;
    successTitle: string;
    successSubtitle: string;
    privacyNote: string;
    sending: string;
    sendMessage: string;
    errors: { required: string };
    form: {
      nameLabel: string;
      namePlaceholder: string;
      phoneLabel: string;
      phonePlaceholder: string;
      emailLabel: string;
      emailPlaceholder: string;
      serviceLabel: string;
      servicePlaceholder: string;
      budgetLabel: string;
      budgetPlaceholder: string;
      messageLabel: string;
      messagePlaceholder: string;
    };
  };
  footer: {
    languageLabel: string;
    emailTitle: string;
    callTitle: string;
    officeLocationTitle: string;
    infoInquiryNote: string;
    hiringNote: string;
    hiringSalesNote: string;
    generalEmailLabel: string;
    careersEmailLabel: string;
    // securityPoliciesTitle: string;
    // pdfDocumentLabel: string;
    privacyPolicy: string;
    collaborations: string;
    reviews: string;
    termsOfUse: string;
    protectFromFraud: string;
    accessibility: string;
    backToTop: string;
    ctaLine1: string;
    ctaLine2: string;
    menuTitle: string;
    connectTitle: string;
    areasServedTitle: string;
    askAiAbout: string;
    headquartersLabel: string;
    isoScopeLabel: string;
    allRightsReserved: string;
  };
  inquiry: {
    stepLabel: string;
    step1: { title: string; subtitle: string };
    step2: { title: string; subtitle: string; placeholder: string };
    step3: {
      title: string;
      subtitle: string;
      uploadClick: string;
      uploadDrag: string;
      uploadHint: string;
    };
    step4: {
      title: string;
      subtitle: string;
      fullName: string;
      email: string;
      phone: string;
    };
    services: {
      design: string;
      development: string;
      cloud: string;
      ai: string;
    };
    errors: {
      services: string;
      vision: string;
      name: string;
      emailRequired: string;
      emailInvalid: string;
      phone: string;
      generic: string;
    };
    footer: {
      nextToEnter: string;
      back: string;
      next: string;
      skip: string;
      submit: string;
      submitting: string;
    };
    success: {
      title: string;
      thanks: string;
      message: string;
      backToHome: string;
    };
  };
  /** The response-time promise: shown beside forms, under the closing CTA, in the FAQ. */
  responsePromise: {
    heading: string;
    steps: Array<{ title: string; detail: string }>;
    line: string;
  };
};

const en: Messages = {
  common: {
    viewProject: "View Project",
    processTitle: "Our Development",
    processBoldTitle: "Process",
    ctaGetStarted: "LET'S GET STARTED",
    loading: "Loading",
  },
  home: {
    metaTitle: "AI Software & Web Development Agency for Growth | Woltrio",
    metaDescription:
      "Build secure web, mobile, and AI solutions with Woltrio—product design, development, and automation for teams that move fast. Contact us to start",
    heroHeadline: "Enterprise Software Solutions Built to Scale",
    heroSubheadline:
      "Custom software development, AI-powered solutions, and seamless integrations for modern organizations",
    heroDescription:
      "Woltrio delivers secure, scalable technology that transforms operations, improves outcomes, and reduces complexity. From initial design to full-scale deployment, we build systems that teams trust and users love.",
    heroCtaText: "Schedule Consultation",
    heroCtaText2: "View Our Services",
  },
  allDone: {
    headline: "From Vision to Reality",
    subheadline:
      "At Woltrio, we craft digital experiences that drive growth, engagement, and innovation for your business.",
    services: [
      {
        title: "Digital Product & UI/UX Design",
        description:
          "We create user-centered digital healthcare solutions. From intuitive patient portals to provider dashboards, our design process focuses on usability, accessibility, and compliance. We turn complex clinical workflows into simple, engaging, and efficient digital experiences.",
      },
      {
        title: "HealthTech Web & Mobile Development",
        description:
          "Secure, scalable, and compliant healthcare systems. Our engineering team builds HIPAA-compliant EMR/EHR platforms, telehealth apps, and mobile solutions. Using modern web, mobile, and cloud technologies, we deliver reliable systems that improve care delivery and clinical workflows.",
      },
    ],
  },
  homePage: {
    sectionLabels: {
      services: "Software Development Services",
      whatWeDo: "What We Do",
      projects: "Projects",
      faq: "FAQ",
    },
    hero: {
      ofWorld: "of",
      needsUpdate: "Needs Update",
    },
    movingCardsCta: "VIEW ALL",
    reviewsLink: "Read all client reviews",
    clutchBadgesNote: "Each badge links to the Clutch ranking that lists Woltrio (checked {date}). Rated {rating} from {count} verified Clutch reviews.",
    faq: {
      titleLine1: "Frequently",
      titleLine2: "Asked Questions",
      subtitle:
        "Seeking basic information? Our FAQ section is a ready reckoner with precise answers to the most probable queries.",
    },
    serviceFan: {
      "1": "Frontend",
      "2": "API Development",
      "3": "UI/UX Design",
      "4": "Database Mgt.",
      "5": "MVP",
      "6": "Cloud Solutions",
      "7": "Analytics",
      "8": "DevOps",
    },
    movingCards: {
      "1": {
        title: "EMR & EHR Software Development",
        description:
          "Custom electronic medical record and electronic health record systems designed for your specific workflow and patient population.",
        tags: [
          "Patient data management",
          "Clinical documentation",
          "Order management & tracking",
          "Secure data storage",
        ],
      },
      "2": {
        title: "Healthcare Mobile App Development",
        description:
          "HIPAA-compliant mobile applications connecting patients, providers, and healthcare data across iOS, Android, and web platforms.",
        tags: [
          "Patient engagement apps",
          "Provider mobile solutions",
          "Real-time health monitoring",
          "Telemedicine integrations",
        ],
      },
      "3": {
        title: "Learning Management System Development",
        description:
          "Custom LMS platforms for medical education, staff training, and continuing education programs with analytics and compliance tracking.",
        tags: [
          "Course management",
          "Learning analytics",
          "Certification tracking",
          "Multi-user support",
        ],
      },
      "4": {
        title: "AI & Healthcare Automation Solutions",
        description:
          "Machine learning and AI models for clinical decision support, predictive analytics, and healthcare process automation.",
        tags: [
          "Predictive diagnostics",
          "Workflow automation",
          "NLP for clinical notes",
          "Data insights & analytics",
        ],
      },
      "5": {
        title: "UI/UX Digital Product Design",
        description:
          "User-centered healthcare interface design that improves usability, reduces clinician burden, and enhances patient experience.",
        tags: [
          "Healthcare UX research",
          "Clinical workflow design",
          "Accessibility-first design",
          "Design systems & components",
        ],
      },
      "6": {
        title: "API Development & System Integration",
        description:
          "Secure healthcare system integration leveraging HL7, FHIR, and custom APIs for seamless data exchange across enterprise platforms.",
        tags: [
          "HL7 integration",
          "FHIR implementation",
          "Legacy system modernization",
          "Secure data exchange",
        ],
      },
    },
    accordion: {
      "1": {
        title: "HL7 INTEGRATION",
        description:
          "Complete HL7 v2 and v3 implementation for healthcare message exchange, ensuring your systems communicate reliably across enterprise architecture.",
      },
      "2": {
        title: "FHIR STANDARDS",
        description:
          "Fast Healthcare Interoperability Resources implementation for modern, standards-based healthcare data exchange and API development.",
      },
      "3": {
        title: "HIPAA COMPLIANCE",
        description:
          "Security and privacy by design. All integrations include encryption, audit logging, and comprehensive HIPAA compliance measures.",
      },
      "4": {
        title: "SECURE APIs",
        description:
          "Healthcare-grade API development with OAuth 2.0, encryption, rate limiting, and comprehensive security controls for safe data access.",
      },
      "5": {
        title: "HEALTH DATA EXCHANGE",
        description:
          "Unified health information infrastructure enabling real-time data sharing between hospitals, clinics, pharmacies, and payers.",
      },
      "6": {
        title: "LEGACY MODERNIZATION",
        description:
          "Transform outdated healthcare systems with API-first architecture, cloud migration, and modern integration patterns.",
      },
    },
    featuredProjects: {
      "vine-university-lms": {
        title: "Vine - University LMS",
        description:
          "A comprehensive management system designed for students, instructors, and universities to streamline education workflows.",
      },
      "marketintent-data-automation": {
        title: "MarketIntent - Data Automation",
        description:
          "An automation platform for data scraping and management, utilizing customized scripts and spider crawling technologies.",
      },
      "trendsetgo-ai-trends-analyzer": {
        title: "TrendSetGo - AI Trends Analyzer",
        description:
          "An intelligent trends analyzer leveraging Apollo data to provide actionable market insights.",
      },
    },
    whyChoose: {
      title: "Why Healthcare Organizations Choose Woltrio",
      description:
        "Woltrio is a healthcare technology company specializing in custom software development for healthcare providers, health systems, and digital health companies. We combine deep healthcare industry knowledge with modern software engineering to create solutions that improve patient outcomes and operational efficiency.",
      description2:
        "Our team understands healthcare workflows, compliance requirements, and the critical importance of data security. We don't just build software—we build healthcare systems that your teams will actually use and love.",
      features: [
        "Expertise in EMR, EHR, and healthcare app development",
        "HL7, FHIR, and healthcare integration specialists",
        "HIPAA-first development with security by design",
        "Proven track record with healthcare enterprises",
        "Agile methodology with dedicated healthcare teams",
      ],
    },
    benefits: {
      title: "Why Choose Woltrio",
      subtitle:
        "We're not just developers we're healthcare technology partners committed to your organization's success.",
      items: [
        {
          title: "Security First",
          description:
            "Enterprise-grade security architecture with encryption, authentication, and audit trails built into every system from day one.",
        },
        {
          title: "Scalable Architecture",
          description:
            "Cloud-native systems designed to scale from startup to enterprise, handling millions of patient records and transactions.",
        },
        {
          title: "Healthcare Expertise",
          description:
            "Deep understanding of healthcare workflows, compliance requirements, and clinical operations. We speak healthcare.",
        },
        {
          title: "Dedicated Teams",
          description:
            "Dedicated development teams focused exclusively on your project, bringing continuity and deep product knowledge.",
        },
        {
          title: "User-Centered Design",
          description:
            "Healthcare UX experts who understand clinician workflows, reducing burden and improving adoption rates.",
        },
        {
          title: "Compliance & Certification",
          description:
            "HIPAA, GDPR, and healthcare-specific compliance built into development, testing, and deployment, with systems designed to support your SOC 2 audit.",
        },
      ],
    },
    process: {
      title: "Our Development Process",
      subtitle:
        "A proven methodology for delivering healthcare software that meets your needs, timelines, and compliance requirements.",
      steps: [
        {
          title: "Discovery & Planning",
          description:
            "Deep dive into your workflows, requirements, and healthcare ecosystem. Define scope, timeline, and success metrics.",
        },
        {
          title: "Strategy & Architecture",
          description:
            "Design secure, scalable architecture. Plan integrations, compliance measures, and technology stack selection.",
        },
        {
          title: "Design & Prototyping",
          description:
            "Create user flows, wireframes, and high-fidelity prototypes. Validate with clinicians and stakeholders.",
        },
        {
          title: "Development & Integration",
          description:
            "Agile sprints with continuous testing, security reviews, and integration with your existing healthcare systems.",
        },
        {
          title: "Launch & Optimization",
          description:
            "Deployment, user training, ongoing optimization, and 24/7 support to ensure smooth operation and user adoption.",
        },
      ],
    },
    industries: {
      title: "Industries We Serve",
      subtitle:
        "We have deep expertise across the healthcare ecosystem, from large hospital systems to emerging healthtech startups.",
      items: [
        "Hospitals & Health Systems",
        "Clinics & Urgent Care",
        "HealthTech Startups",
        "Medical Education",
        "EdTech Companies",
        "SaaS Healthcare Platforms",
        "Medical Device Companies",
        "Pharmaceutical Companies",
      ],
    },
  },
  workPage: {
    metaTitle: "Portfolio & Case Studies",
    metaDescription:
      "Explore Woltrio’s portfolio and case studies to see how we design, build, and ship high-quality digital products for growing teams.",
    hero: {
      title: "Projects we've",
      boldTitle: "delivered for clients",
      subtitle: "Case studies from healthcare, fintech, edtech and AI, with the results each client got.",
    },
    intro: {
      title: "Real Products, Built to Scale",
      paragraphs: [
        "Every project here started the same way our engagements always do: a discovery conversation about the workflow you're trying to fix, the users you're building for, and the constraints — compliance, timeline, legacy systems — you're working within. From there, our teams design, build, and ship production software, not prototypes that stall after the demo.",
        "The work spans HIPAA and HL7/FHIR-compliant healthcare platforms, AI-powered automation tools, SaaS products, and learning management systems, each built by a dedicated team that stays with the project from architecture through launch and post-launch support. Explore a project below, or get in touch to talk through what you're building next.",
      ],
    },
    cta: {
      title: "Stunning Design, Ready When You Need Them",
      subtitle: "at an affordable rate to help scale your business.",
      buttonText: "CONTACT US",
    },
    learnMore: "Learn more",
    categories: {
      ALL: "ALL",
      SAAS: "SAAS",
      WEBSITES: "WEBSITES",
      "WEB APPS": "WEB APPS",
      AI: "AI",
      "MOBILE APPS": "MOBILE APPS",
      BRANDING: "BRANDING",
      "UX DISCOVERY": "UX DISCOVERY",
      MVP: "MVP",
    },
    notFound: {
      metaTitle: "Project Not Found",
      metaDescription:
        "The requested project could not be found. Contact Woltrio if you need help or want to discuss a similar build.",
      title: "Project Not Found",
      back: "Back to Case Studies",
    },
    details: {
      backToAllProjects: "Back to All Projects",
      viewLiveProject: "View Live Project",
      caseStudySuffix: "Case Study",
      defaultMetaDescription:
        "Explore this project case study to see how Woltrio designs, builds, and ships reliable digital products that solve real business challenges.",
      industryLabel: "Industry",
      headquartersLabel: "Headquarters",
      platformLabel: "Platform",
      challengeTitle: "The Business Challenge We Solved",
      solutionTitle: "The Technical Solution We Delivered",
      impactTitle: "Project Impact and Measurable Results",
      impact: {
        efficiencyIncrease: "Efficiency Increase",
        userEngagement: "User Engagement",
        activeUsers: "Active Users",
      },
      galleryItemAlt: "Gallery item",
      ctaTitle: "Tell us about your project",
      ctaDescription:
        "Have a similar challenge? Let's talk through what you're building and how our team can help you ship it.",
    },
    projects: {
      "vine-university-lms": {
        title: "Vine - University LMS",
        description:
          "A comprehensive management system designed for students, instructors, and universities to streamline education workflows.",
        fullDescription:
          "Vine is a state-of-the-art Learning Management System (LMS) tailored for modern universities. It bridges the gap between administration, faculty, and students by providing a unified platform for course management, grading, and communication.",
        challenge:
          "Universities often struggle with fragmented systems where student data, course materials, and grades are scattered across different platforms. This leads to administrative inefficiencies and a poor user experience for students.",
        solution:
          "We built a centralized, cloud-native platform using Next.js and AdonisJS. Vine integrates real-time notifications, automated grading workflows, and a responsive student portal, resulting in a 40% reduction in administrative overhead.",
      },
      "marketintent-data-automation": {
        title: "MarketIntent - Data Automation",
        description:
          "An automation platform for data scraping and management, utilizing customized scripts and spider crawling technologies.",
        fullDescription:
          "MarketIntent is a powerful data intelligence tool that automates the extraction of competitive market data. It empowers businesses to make informed decisions by providing real-time insights into market trends and competitor pricing.",
        challenge:
          "Manual data collection is time-consuming and prone to errors. Businesses needed a way to gather vast amounts of market data quickly and accurately without dedicating significant human resources.",
        solution:
          "We developed a scalable scraping engine using Puppeteer and FastAPI. The system manages thousands of concurrent scraping jobs via PM2, cleaning and structuring the data automatically for immediate analysis.",
      },
      "trendsetgo-ai-trends-analyzer": {
        title: "TrendSetGo - AI Trends Analyzer",
        description:
          "An intelligent trends analyzer leveraging Apollo data to provide actionable market insights.",
        fullDescription:
          "TrendSetGo utilizes advanced AI algorithms to analyze social media and market data, identifying emerging trends before they go mainstream. It's designed for marketers and product developers looking for the next big thing.",
        challenge:
          "Identifying trends early is difficult due to the sheer volume of noise on social media. Traditional analytics tools are often reactive rather than predictive.",
        solution:
          "By integrating Apollo data with custom AI models served via FastAPI, TrendSetGo filters out the noise. It provides a predictive score for potential trends, allowing users to capitalize on opportunities weeks before competitors.",
      },
      "daisy-ai-intelligent-emr": {
        title: "Daisy.ai - Intelligent EMR",
        description:
          "A professional EMR (Electronic Medical Records) system integrated with a customized AI platform for healthcare efficiency.",
        fullDescription:
          "Daisy.ai reimagines the Electronic Medical Record system by embedding AI assistance directly into the clinician's workflow. It helps with documentation, diagnosis suggestions, and patient follow-ups.",
        challenge:
          "Physicians spend nearly 50% of their time on paperwork, leading to burnout and less time for patient care. Existing EMRs are clunky and unintuitive.",
        solution:
          "We created a streamlined, voice-enabled EMR interface. The AI backend listens to patient consultations (with consent) and automatically drafts clinical notes, which the doctor simply reviews and signs, saving hours of daily administrative work.",
      },
      "billingvista---billing-solutions": {
        title: "BillingVista - Billing Solutions",
        description:
          "A modern homepage and interface design for a comprehensive billing system platform.",
        fullDescription:
          "BillingVista is a fintech platform designed to simplify complex billing and subscription management for SaaS companies. It offers a sleek, user-friendly dashboard for managing invoices and payments.",
        challenge:
          "Complex billing logic (proration, usage-based billing) often results in confusing invoices and customer churn. The client needed a UI that made these complex concepts feel simple.",
        solution:
          "We focused heavily on UX/UI design, creating a visual language that clarifies billing cycles. The new dashboard allows users to visualize their usage and costs in real-time, increasing transparency and trust.",
      },
      "aelapro-job-notifications": {
        title: "AelaPro - Job Notifications",
        description:
          "A smart notification provider that connects applicants with job opportunities in real-time.",
        fullDescription:
          "AelaPro is a career acceleration tool that monitors job boards 24/7. It instantly notifies job seekers when a role matching their specific criteria is posted, giving them a first-mover advantage.",
        challenge:
          "In a competitive job market, timing is everything. Manually refreshing job boards is inefficient and leads to missed opportunities.",
        solution:
          "We built a real-time notification engine that aggregates listings from major job sites. Users set granular filters, and the system pushes alerts via email or SMS within seconds of a new posting.",
      },
    },
  },
  nav: {
    services: "Services",
    solutions: "Solutions",
    work: "Case Studies",
    insights: "Insights",
    team: "Team",
    careers: "Careers",
    about: "About",
    contact: "Contact",
    inquiryNow: "INQUIRY NOW",
    viewAllServices: "VIEW ALL SERVICES",
    viewAllSolutions: "VIEW ALL SOLUTIONS",
    servicesDropdownHint: "Want to develop your digital product? Let's connect!",
    solutionsDropdownHint: "Explore our end-to-end solutions for your business.",
    healthcare: "Healthcare",
    healthcarePanelEyebrow: "Healthcare software engineering",
    healthcarePanelTitle: "Built for regulated care",
    healthcarePanelIso: "ISO/IEC 27001:2022 certified development center",
    healthcarePanelBaa: "We sign a Business Associate Agreement (BAA) with healthcare clients before PHI is shared.",
    exploreHealthcare: "Explore healthcare",
    seeCaseStudies: "See case studies",
    servicesPanelEyebrow: "What we do",
    servicesPanelTitle: "AI agents and custom software",
    servicesPanelBody: "Agents, automation and web, mobile and cloud products, built to healthcare standards.",
    exploreServices: "Explore services",
    company: "Company",
    companyPanelEyebrow: "Start a project",
    companyPanelTitle: "Have a project in mind?",
    companyPanelBody: "Share your goals and timeline, and we'll plan the next step with you.",
    socialsTitle: "Find us on",
  },
  pages: {
    services: {
      our: "Our",
      title: "Services",
      subtitle:
        "We build end-to-end digital solutions, from stunning front-ends to intelligent AI backends. Explore our capabilities.",
      learnMore: "Learn More",
    },
    solutions: {
      our: "Our",
      title: "Solutions",
      subtitle:
        "We build end-to-end digital solutions, from stunning front-ends to intelligent AI backends. Explore our capabilities.",
      learnMore: "Learn More",
    },
    relatedInsights: {
      eyebrow: "Related insights",
      title: "Further reading",
      allInsights: "All insights",
    },
    relatedLinks: {
      eyebrow: "Keep exploring",
      title: "Related pages",
      specialtiesEyebrow: "Healthcare",
      specialtiesTitle: "Software by specialty",
      siblingsEyebrow: "More in this service",
      overview: "Overview",
    },
  },
  contact: {
    kicker: "CONTACT",
    title: "Get in touch with Woltrio",
    bookMeeting: "Book a Meeting on Calendly",
    supportEmail: "Support email",
    callAnytime: "Toll Free Number",
    liveChat: "Live Chat",
    whatsappContact: "Chat on WhatsApp",
    successTitle: "Message Sent!",
    successSubtitle:
      "Thank you for contacting us. We will get back to you as soon as possible.",
    privacyNote: "*We promise not to disclose your personal information to third parties.",
    sending: "SENDING...",
    sendMessage: "SEND MESSAGE",
    errors: { required: "Please fill in all required fields." },
    form: {
      nameLabel: "NAME*",
      namePlaceholder: "Your name",
      phoneLabel: "PHONE NUMBER (OPTIONAL)",
      phonePlaceholder: "(555) 555-5555",
      emailLabel: "EMAIL*",
      emailPlaceholder: "your@email.com",
      serviceLabel: "SELECT SERVICE TYPE*",
      servicePlaceholder: "Please select a service",
      budgetLabel: "PROJECT BUDGET*",
      budgetPlaceholder: "Select your budget",
      messageLabel: "MESSAGE*",
      messagePlaceholder: "Your message...",
    },
  },
  footer: {
    languageLabel: "Language",
    emailTitle: "Email",
    callTitle: "Call",
    officeLocationTitle: "Office Location",
    infoInquiryNote: "(For info Inquiry)",
    hiringNote: "(For Hiring)",
    hiringSalesNote: "(For Hiring/Sales)",
    generalEmailLabel: "Email Us",
    careersEmailLabel: "Careers",
    // securityPoliciesTitle: "Security & Compliance Policies",
    // pdfDocumentLabel: "PDF Document",
    privacyPolicy: "Privacy policy",
    collaborations: "Collaborations",
    reviews: "Client reviews",
    termsOfUse: "Terms of Use",
    protectFromFraud: "Fraud Protection",
    accessibility: "Accessibility",
    backToTop: "Back to the top",
    ctaLine1: "Grow with Woltrio.",
    ctaLine2: "Start your journey today.",
    menuTitle: "Menu",
    connectTitle: "Connect",
    areasServedTitle: "Areas We Serve",
    askAiAbout: "Ask AI about",
    headquartersLabel: "Headquarters",
    isoScopeLabel: "ISO/IEC 27001:2022 certified development center",
    allRightsReserved: "All rights reserved.",
  },
  inquiry: {
    stepLabel: "of",
    step1: {
      title: "Pick your path to a stunning experience!",
      subtitle:
        "Start your journey with us share your needs, and we'll shape the perfect solution together!",
    },
    step2: {
      title: "Tell us what you're dreaming of",
      subtitle:
        "Share your vision with us, and we'll turn your dream into a beautifully crafted reality!",
      placeholder: "Type your message here... ",
    },
    step3: {
      title: "Have any docs? Share them to bring your vision to life!",
      subtitle: "Upload your drafts to help us craft a perfect design solution!",
      uploadClick: "Click to upload",
      uploadDrag: "or drag and drop",
      uploadHint: "PDF, DOC, TXT, or ZIP (MAX. 5MB)",
    },
    step4: {
      title: "It's time to ignite the spark to turn your ideas into reality!",
      subtitle: "Share your contact details, and we'll connect to discuss your vision!",
      fullName: "Full Name",
      email: "Email Address",
      phone: "Phone Number (Optional)",
    },
    services: {
      design: "Design",
      development: "Development",
      cloud: "Cloud & Devops",
      ai: "AI Solutions",
    },
    errors: {
      services: "Please select at least one service to proceed.",
      vision: "Please describe your vision.",
      name: "Full Name is required.",
      emailRequired: "Email Address is required.",
      emailInvalid: "Please enter a valid email address.",
      phone: "Please enter a valid phone number (min 10 digits).",
      generic: "Something went wrong. Please try again.",
    },
    footer: {
      nextToEnter: "Next to enter",
      back: "BACK",
      next: "NEXT",
      skip: "SKIP",
      submit: "SUBMIT",
      submitting: "SUBMITTING",
    },
    success: {
      title: "Inquiry Received!",
      thanks: "Thanks",
      message:
        "We've received your details. A member of our team will reply the same business day.",
      backToHome: "Back to Home",
    },
  },
  responsePromise: {
    heading: "What happens next",
    steps: [
      {
        title: "A reply the same business day",
        detail: "A member of our team reads your message and replies on the business day it arrives.",
      },
      {
        title: "NDA within 24 hours",
        detail:
          "If you'd like one before sharing details, we sign your NDA, or ours, within 24 hours. On healthcare projects we also sign a BAA before any PHI is shared.",
      },
      {
        title: "A free scope within 3 business days",
        detail: "After a first call you receive a free scope with a fixed price for each milestone, within 3 business days. Nothing to pay upfront: each milestone is paid after you approve it.",
      },
    ],
    line: "We reply the same business day, sign an NDA within 24 hours, and send a free scope within 3 business days. Nothing to pay upfront.",
  },
};

const de: Messages = {
  common: {
    viewProject: "Projekt ansehen",
    processTitle: "Unser",
    processBoldTitle: "Prozess",
    ctaGetStarted: "JETZT STARTEN",
    loading: "Lädt",
  },
  home: {
    metaTitle: "Woltrio | KI-Softwareagentur",
    metaDescription:
    "Woltrio ist eine führende KI-Softwareagentur für Webentwicklung, Mobile Apps und KI-Lösungen. Wir transformieren Ihr Business mit moderner Technologie.",
    heroHeadline: "Enterprise Healthcare Software Built to Scale",
    heroSubheadline:
      "EMR/EHR-Systeme, KI-gestützte Lösungen und maßgeschneiderte Integrationen für moderne Gesundheitsorganisationen",
    heroDescription:
      "Woltrio liefert HIPAA-konforme Healthcare-Technologie, die Patientenversorgung transformiert, Ergebnisse verbessert und operative Komplexität reduziert. Von der ersten Planung bis zur vollständigen Implementierung bauen wir Systeme, denen Healthcare-Teams vertrauen.",
    heroCtaText: "Beratung vereinbaren",
    heroCtaText2: "Unsere Services ansehen",
  },
  allDone: {
    headline: "Von der Vision zur Realität",
    subheadline:
      "Bei Woltrio entwickeln wir digitale Erlebnisse, die Wachstum, Engagement und Innovation für Ihr Unternehmen vorantreiben.",
    services: [
      {
        title: "Digital Product & UI/UX Design",
        description:
          "Wir gestalten nutzerzentrierte digitale Healthcare‑Lösungen – von intuitiven Patientenportalen bis zu Provider‑Dashboards. Unser Designprozess fokussiert Usability, Barrierefreiheit und Compliance. So machen wir komplexe klinische Workflows einfach, ansprechend und effizient.",
      },
      {
        title: "HealthTech Web- & Mobile-Entwicklung",
        description:
          "Sichere, skalierbare und compliant Healthcare‑Systeme. Unser Engineering‑Team baut HIPAA‑konforme EMR/EHR‑Plattformen, Telehealth‑Apps und Mobile‑Lösungen. Mit modernen Web‑, Mobile‑ und Cloud‑Technologien liefern wir robuste Systeme, die Versorgung und klinische Abläufe verbessern.",
      },
    ],
  },
  homePage: {
    sectionLabels: {
      services: "Services",
      whatWeDo: "Was wir tun",
      projects: "Projekte",
      faq: "FAQ",
    },
    hero: {
      ofWorld: "der",
      needsUpdate: "braucht ein Update",
    },
    movingCardsCta: "ALLE ANSEHEN",
    reviewsLink: "Alle Kundenbewertungen lesen",
    clutchBadgesNote: "Jedes Abzeichen verlinkt auf das Clutch-Ranking, in dem Woltrio aufgeführt ist (geprüft am {date}). Bewertet mit {rating} aus {count} verifizierten Clutch-Bewertungen.",
    faq: {
      titleLine1: "Häufig",
      titleLine2: "gestellte Fragen",
      subtitle:
        "Sie suchen Basisinfos? In unseren FAQs finden Sie präzise Antworten auf die wahrscheinlichsten Fragen.",
    },
    serviceFan: {
      "1": "Frontend",
      "2": "API‑Entwicklung",
      "3": "UI/UX‑Design",
      "4": "Datenbank",
      "5": "MVP",
      "6": "Cloud‑Lösungen",
      "7": "Analytics",
      "8": "DevOps",
    },
    movingCards: {
      "1": {
        title: "📋 EMR/EHR-Softwareentwicklung",
        description:
          "Maßgeschneiderte elektronische Patientenakten- und Gesundheitsakten-Systeme, die auf Ihren spezifischen Workflow und Ihre Patientenpopulation zugeschnitten sind.",
        tags: [
          "Patientendatenverwaltung",
          "Klinische Dokumentation",
          "Auftragsverwaltung & Tracking",
          "Sichere Datenspeicherung",
        ],
      },
      "2": {
        title: "📱 Healthcare Mobile App-Entwicklung",
        description:
          "HIPAA-konforme mobile Anwendungen, die Patienten, Anbieter und Gesundheitsdaten über iOS, Android und Web-Plattformen verbinden.",
        tags: [
          "Patienten-Engagement-Apps",
          "Provider-Mobile-Lösungen",
          "Echtzeit-Gesundheitsüberwachung",
          "Telemedizin-Integrationen",
        ],
      },
      "3": {
        title: "🎓 Learning Management System-Entwicklung",
        description:
          "Maßgeschneiderte LMS-Plattformen für medizinische Ausbildung, Mitarbeiterschulung und Weiterbildungsprogramme mit Analytics und Compliance-Tracking.",
        tags: [
          "Kursverwaltung",
          "Lern-Analytics",
          "Zertifizierungs-Tracking",
          "Multi-User-Support",
        ],
      },
      "4": {
        title: "🤖 KI & Healthcare-Automatisierungslösungen",
        description:
          "Machine Learning und KI-Modelle für klinische Entscheidungsunterstützung, prädiktive Analytik und Healthcare-Prozessautomatisierung.",
        tags: [
          "Prädiktive Diagnostik",
          "Workflow-Automatisierung",
          "NLP für klinische Notizen",
          "Daten-Insights & Analytics",
        ],
      },
      "5": {
        title: "🎨 UI/UX Digital Product Design",
        description:
          "Nutzerzentriertes Healthcare-Interface-Design, das Benutzerfreundlichkeit verbessert, Kliniker-Belastung reduziert und Patientenerfahrung verbessert.",
        tags: [
          "Healthcare-UX-Forschung",
          "Klinisches Workflow-Design",
          "Accessibility-First-Design",
          "Design-Systeme & Komponenten",
        ],
      },
      "6": {
        title: "⚙️ API-Entwicklung & Systemintegration",
        description:
          "Sichere Healthcare-Systemintegration mit HL7, FHIR und benutzerdefinierten APIs für nahtlosen Datenaustausch über Enterprise-Plattformen.",
        tags: [
          "HL7-Integration",
          "FHIR-Implementierung",
          "Legacy-System-Modernisierung",
          "Sicherer Datenaustausch",
        ],
      },
    },
    accordion: {
      "1": {
        title: "HL7-INTEGRATION",
        description:
          "Vollständige HL7 v2- und v3-Implementierung für Healthcare-Nachrichtenaustausch, um sicherzustellen, dass Ihre Systeme zuverlässig über die Enterprise-Architektur kommunizieren.",
      },
      "2": {
        title: "FHIR-STANDARDS",
        description:
          "Fast Healthcare Interoperability Resources-Implementierung für modernen, standardbasierten Healthcare-Datenaustausch und API-Entwicklung.",
      },
      "3": {
        title: "HIPAA-COMPLIANCE",
        description:
          "Sicherheit und Datenschutz by Design. Alle Integrationen umfassen Verschlüsselung, Audit-Logging und umfassende HIPAA-Compliance-Maßnahmen.",
      },
      "4": {
        title: "SICHERE APIs",
        description:
          "Healthcare-Grade-API-Entwicklung mit OAuth 2.0, Verschlüsselung, Rate Limiting und umfassenden Sicherheitskontrollen für sicheren Datenzugriff.",
      },
      "5": {
        title: "GESUNDHEITSDATENAUSTAUSCH",
        description:
          "Einheitliche Gesundheitsinformationsinfrastruktur, die Echtzeit-Datenaustausch zwischen Krankenhäusern, Kliniken, Apotheken und Kostenträgern ermöglicht.",
      },
      "6": {
        title: "LEGACY-MODERNISIERUNG",
        description:
          "Transformation veralteter Healthcare-Systeme mit API-First-Architektur, Cloud-Migration und modernen Integrationsmustern.",
      },
    },
    featuredProjects: {
      "vine-university-lms": {
        title: "Vine – Universitäts‑LMS",
        description:
          "Ein umfassendes Managementsystem für Studierende, Lehrende und Universitäten – zur Vereinfachung von Lehr‑ und Verwaltungsprozessen.",
      },
      "marketintent-data-automation": {
        title: "MarketIntent – Datenautomatisierung",
        description:
          "Eine Automationsplattform für Datenscraping und -management mit individuellen Skripten und Spider‑Crawling‑Technologien.",
      },
      "trendsetgo-ai-trends-analyzer": {
        title: "TrendSetGo – KI‑Trend‑Analyzer",
        description:
          "Ein intelligenter Trend‑Analyzer, der Apollo‑Daten nutzt, um umsetzbare Markt‑Insights zu liefern.",
      },
    },
    whyChoose: {
      title: "Warum Gesundheitsorganisationen Woltrio wählen",
      description:
        "Woltrio ist ein Healthcare-Technologieunternehmen, spezialisiert auf maßgeschneiderte Softwareentwicklung für Gesundheitsdienstleister, Gesundheitssysteme und Digital-Health-Unternehmen. Wir kombinieren tiefes Branchenwissen mit moderner Software-Engineering, um Lösungen zu schaffen, die Patientenergebnisse und operative Effizienz verbessern.",
      description2:
        "Unser Team versteht Healthcare-Workflows, Compliance-Anforderungen und die kritische Bedeutung von Datensicherheit. Wir bauen nicht nur Software – wir bauen Healthcare-Systeme, die Ihre Teams tatsächlich nutzen und lieben werden.",
      features: [
        "Expertise in EMR, EHR und Healthcare-App-Entwicklung",
        "HL7, FHIR und Healthcare-Integrationsspezialisten",
        "HIPAA-first Entwicklung mit Security by Design",
        "Nachgewiesene Erfolgsbilanz mit Healthcare-Unternehmen",
        "Agile Methodik mit dedizierten Healthcare-Teams",
      ],
    },
    benefits: {
      title: "Warum Woltrio wählen",
      subtitle:
        "Wir sind nicht nur Entwickler – wir sind Healthcare-Technologie-Partner, die sich dem Erfolg Ihrer Organisation verpflichtet haben.",
      items: [
        {
          title: "Sicherheit zuerst",
          description:
            "Enterprise-Grade-Sicherheitsarchitektur mit Verschlüsselung, Authentifizierung und Audit-Trails von Anfang an in jedem System integriert.",
        },
        {
          title: "Skalierbare Architektur",
          description:
            "Cloud-native Systeme, die von Startup bis Enterprise skalieren und Millionen von Patientenakten und Transaktionen verarbeiten.",
        },
        {
          title: "Healthcare-Expertise",
          description:
            "Tiefes Verständnis von Healthcare-Workflows, Compliance-Anforderungen und klinischen Abläufen. Wir sprechen Healthcare.",
        },
        {
          title: "Dedizierte Teams",
          description:
            "Dedizierte Entwicklungsteams, die sich ausschließlich auf Ihr Projekt konzentrieren und Kontinuität und tiefes Produktwissen mitbringen.",
        },
        {
          title: "Nutzerzentriertes Design",
          description:
            "Healthcare-UX-Experten, die Kliniker-Workflows verstehen, Belastungen reduzieren und Akzeptanzraten verbessern.",
        },
        {
          title: "Compliance & Zertifizierung",
          description:
            "HIPAA-, DSGVO- und healthcare-spezifische Compliance in Entwicklung, Testing und Deployment integriert, mit Systemen, die Ihr SOC-2-Audit unterstützen.",
        },
      ],
    },
    process: {
      title: "Unser Entwicklungsprozess",
      subtitle:
        "Eine bewährte Methodik zur Bereitstellung von Healthcare-Software, die Ihre Anforderungen, Zeitpläne und Compliance-Anforderungen erfüllt.",
      steps: [
        {
          title: "Discovery & Planung",
          description:
            "Tiefgehende Analyse Ihrer Workflows, Anforderungen und Healthcare-Ökosystems. Definition von Umfang, Zeitplan und Erfolgsmetriken.",
        },
        {
          title: "Strategie & Architektur",
          description:
            "Design sicherer, skalierbarer Architektur. Planung von Integrationen, Compliance-Maßnahmen und Technologie-Stack-Auswahl.",
        },
        {
          title: "Design & Prototyping",
          description:
            "Erstellung von User Flows, Wireframes und High-Fidelity-Prototypen. Validierung mit Klinikern und Stakeholdern.",
        },
        {
          title: "Entwicklung & Integration",
          description:
            "Agile Sprints mit kontinuierlichem Testing, Security-Reviews und Integration in Ihre bestehenden Healthcare-Systeme.",
        },
        {
          title: "Launch & Optimierung",
          description:
            "Deployment, Benutzerschulung, laufende Optimierung und 24/7-Support für reibungslosen Betrieb und Benutzerakzeptanz.",
        },
      ],
    },
    industries: {
      title: "Branchen, die wir bedienen",
      subtitle:
        "Wir haben tiefe Expertise im gesamten Healthcare-Ökosystem, von großen Krankenhaussystemen bis zu aufstrebenden HealthTech-Startups.",
      items: [
        "Krankenhäuser & Gesundheitssysteme",
        "Kliniken & Notfallversorgung",
        "HealthTech-Startups",
        "Medizinische Ausbildung",
        "EdTech-Unternehmen",
        "SaaS Healthcare-Plattformen",
        "Medizingerätehersteller",
        "Pharmaunternehmen",
      ],
    },
  },
  workPage: {
    metaTitle: "Portfolio & Fallstudien",
    metaDescription:
      "Entdecken Sie Woltrios Portfolio und Fallstudien: So entwerfen, bauen und liefern wir hochwertige digitale Produkte für wachsende Teams.",
    hero: {
      title: "Projekte, die wir",
      boldTitle: "für Kunden umgesetzt haben",
      subtitle:
        "Fallstudien aus Healthcare, Fintech, EdTech und KI – mit den Ergebnissen für jeden Kunden.",
    },
    intro: {
      title: "Echte Produkte, für Wachstum gebaut",
      paragraphs: [
        "Jedes Projekt hier begann genauso wie unsere Zusammenarbeit immer beginnt: mit einem Gespräch über den Workflow, den Sie verbessern möchten, die Nutzer, für die Sie bauen, und die Rahmenbedingungen — Compliance, Zeitplan, Altsysteme —, innerhalb derer Sie arbeiten. Von dort aus entwerfen, bauen und liefern unsere Teams produktionsreife Software, keine Prototypen, die nach der Demo stehen bleiben.",
        "Die Arbeiten reichen von HIPAA- und HL7/FHIR-konformen Gesundheitsplattformen über KI-gestützte Automatisierungstools bis hin zu SaaS-Produkten und Learning-Management-Systemen — jedes von einem dedizierten Team betreut, das von der Architektur bis zum Launch und darüber hinaus dabeibleibt. Entdecken Sie ein Projekt unten oder kontaktieren Sie uns.",
      ],
    },
    cta: {
      title: "Starkes Design – genau dann, wenn Sie es brauchen",
      subtitle: "zu einem fairen Preis, damit Ihr Business skalieren kann.",
      buttonText: "KONTAKT",
    },
    learnMore: "Mehr erfahren",
    categories: {
      ALL: "ALLE",
      SAAS: "SaaS",
      WEBSITES: "Websites",
      "WEB APPS": "Web‑Apps",
      AI: "KI",
      "MOBILE APPS": "Mobile Apps",
      BRANDING: "Branding",
      "UX DISCOVERY": "UX‑Discovery",
      MVP: "MVP",
    },
    notFound: {
      metaTitle: "Projekt nicht gefunden",
      metaDescription:
        "Das angefragte Projekt konnte nicht gefunden werden. Kontaktieren Sie Woltrio, wenn Sie Hilfe benötigen oder ein ähnliches Projekt besprechen möchten.",
      title: "Projekt nicht gefunden",
      back: "Zurück zu den Projekten",
    },
    details: {
      backToAllProjects: "Zurück zu allen Projekten",
      viewLiveProject: "Live‑Projekt ansehen",
      caseStudySuffix: "Fallstudie",
      defaultMetaDescription:
        "Entdecken Sie diese Fallstudie und sehen Sie, wie Woltrio verlässliche digitale Produkte gestaltet, entwickelt und ausliefert – für echte Business‑Herausforderungen.",
      industryLabel: "Branche",
      headquartersLabel: "Hauptsitz",
      platformLabel: "Plattform",
      challengeTitle: "Die Business‑Herausforderung, die wir gelöst haben",
      solutionTitle: "Die technische Lösung, die wir geliefert haben",
      impactTitle: "Projektwirkung und messbare Ergebnisse",
      impact: {
        efficiencyIncrease: "Effizienzsteigerung",
        userEngagement: "Nutzer‑Engagement",
        activeUsers: "Aktive Nutzer",
      },
      galleryItemAlt: "Galerie‑Element",
      ctaTitle: "Erzählen Sie uns von Ihrem Projekt",
      ctaDescription:
        "Haben Sie eine ähnliche Herausforderung? Lassen Sie uns besprechen, was Sie bauen möchten und wie unser Team Sie dabei unterstützen kann.",
    },
    projects: {
      "vine-university-lms": {
        title: "Vine – Universitäts‑LMS",
        description:
          "Ein umfassendes Managementsystem für Studierende, Lehrende und Universitäten – zur Vereinfachung von Lehr‑ und Verwaltungsprozessen.",
        fullDescription:
          "Vine ist ein modernes Learning‑Management‑System (LMS) für Universitäten. Es verbindet Verwaltung, Lehrende und Studierende über eine zentrale Plattform für Kursmanagement, Noten und Kommunikation.",
        challenge:
          "Universitäten kämpfen oft mit fragmentierten Systemen, in denen Studierendendaten, Kursmaterialien und Noten auf verschiedene Plattformen verteilt sind. Das führt zu ineffizienter Verwaltung und einer schlechteren Nutzererfahrung.",
        solution:
          "Wir haben eine zentrale, cloud‑native Plattform mit Next.js und AdonisJS entwickelt. Vine integriert Echtzeit‑Benachrichtigungen, automatisierte Bewertungsworkflows und ein responsives Studierendenportal – mit 40% weniger administrativem Aufwand.",
      },
      "marketintent-data-automation": {
        title: "MarketIntent – Datenautomatisierung",
        description:
          "Eine Automationsplattform für Datenscraping und -management mit individuellen Skripten und Spider‑Crawling‑Technologien.",
        fullDescription:
          "MarketIntent ist ein leistungsstarkes Data‑Intelligence‑Tool, das die Extraktion von Wettbewerbs- und Marktdaten automatisiert und in Echtzeit Insights zu Trends und Preisen liefert.",
        challenge:
          "Manuelle Datenerhebung ist langsam und fehleranfällig. Unternehmen brauchten eine Möglichkeit, große Datenmengen schnell und präzise zu erfassen, ohne hohe personelle Aufwände.",
        solution:
          "Wir haben eine skalierbare Scraping‑Engine mit Puppeteer und FastAPI gebaut. Über PM2 werden tausende Jobs parallel orchestriert; die Daten werden automatisch bereinigt und strukturiert.",
      },
      "trendsetgo-ai-trends-analyzer": {
        title: "TrendSetGo – KI‑Trend‑Analyzer",
        description:
          "Ein intelligenter Trend‑Analyzer, der Apollo‑Daten nutzt, um umsetzbare Markt‑Insights zu liefern.",
        fullDescription:
          "TrendSetGo analysiert Social‑Media‑ und Marktdaten mit KI‑Algorithmen, um Trends frühzeitig zu erkennen. Ideal für Marketing‑ und Produktteams, die Chancen vor der Konkurrenz nutzen wollen.",
        challenge:
          "Frühe Trendidentifikation ist wegen des hohen Rauschens in sozialen Medien schwierig. Klassische Analytics‑Tools sind oft reaktiv statt prädiktiv.",
        solution:
          "Durch die Kombination von Apollo‑Daten mit eigenen KI‑Modellen (FastAPI) filtert TrendSetGo das Rauschen und liefert einen Prognose‑Score, der Trends Wochen früher sichtbar macht.",
      },
      "daisy-ai-intelligent-emr": {
        title: "Daisy.ai – Intelligentes EMR",
        description:
          "Ein professionelles EMR‑System (Electronic Medical Records) mit integrierter KI‑Plattform zur Steigerung der Effizienz im Gesundheitswesen.",
        fullDescription:
          "Daisy.ai integriert KI‑Assistenz direkt in den klinischen Workflow. Es unterstützt Dokumentation, Diagnose‑Hinweise und Follow‑ups, damit Ärzt:innen mehr Zeit für Patient:innen haben.",
        challenge:
          "Ärzt:innen verbringen oft fast 50% ihrer Zeit mit Dokumentation. Bestehende EMRs sind häufig sperrig und wenig intuitiv – das führt zu Frust und Burnout.",
        solution:
          "Wir entwickelten eine schlanke, sprachgestützte EMR‑Oberfläche. Das KI‑Backend erstellt (mit Einwilligung) automatisch klinische Notizen, die Ärzt:innen nur noch prüfen und signieren müssen.",
      },
      "billingvista---billing-solutions": {
        title: "BillingVista – Billing‑Plattform",
        description:
          "Eine moderne Homepage und UI für eine umfassende Billing‑ und Subscription‑Management‑Plattform.",
        fullDescription:
          "BillingVista vereinfacht komplexes Abrechnungs‑ und Subscription‑Management für SaaS‑Unternehmen – mit einem klaren Dashboard für Rechnungen und Zahlungen.",
        challenge:
          "Komplexe Billing‑Logik (z. B. Proration, Usage‑Based Billing) führt oft zu unklaren Rechnungen und Churn. Der Kunde brauchte eine UI, die Komplexität verständlich macht.",
        solution:
          "Wir fokussierten UX/UI‑Design mit einer visuellen Sprache, die Abrechnungszyklen erklärt. Nutzer sehen Nutzung und Kosten in Echtzeit – mehr Transparenz und Vertrauen.",
      },
      "aelapro-job-notifications": {
        title: "AelaPro – Job‑Benachrichtigungen",
        description:
          "Ein smarter Notification‑Service, der Bewerber:innen in Echtzeit mit passenden Jobchancen verbindet.",
        fullDescription:
          "AelaPro überwacht Jobbörsen rund um die Uhr und benachrichtigt Nutzer:innen sofort bei passenden Rollen – ein echter Zeitvorteil in kompetitiven Märkten.",
        challenge:
          "Timing ist entscheidend. Manuelles Aktualisieren von Jobbörsen ist ineffizient und führt zu verpassten Chancen.",
        solution:
          "Wir bauten eine Echtzeit‑Engine, die Listings aggregiert und innerhalb von Sekunden E‑Mail/SMS‑Alerts ausspielt – basierend auf granularen Filtern.",
      },
    },
  },
  nav: {
    services: "Services",
    solutions: "Lösungen",
    work: "Fallstudien",
    insights: "Insights",
    team: "Team",
    careers: "Karriere",
    about: "Über uns",
    contact: "Kontakt",
    inquiryNow: "ANFRAGE STARTEN",
    viewAllServices: "ALLE SERVICES ANSEHEN",
    viewAllSolutions: "ALLE LÖSUNGEN ANSEHEN",
    servicesDropdownHint:
      "Sie möchten Ihr digitales Produkt entwickeln? Sprechen wir!",
    solutionsDropdownHint:
      "Entdecken Sie unsere End‑to‑End‑Lösungen für Ihr Business.",
    healthcare: "Gesundheitswesen",
    healthcarePanelEyebrow: "Softwareentwicklung für das Gesundheitswesen",
    healthcarePanelTitle: "Gebaut für regulierte Versorgung",
    healthcarePanelIso: "Nach ISO/IEC 27001:2022 zertifiziertes Entwicklungszentrum",
    healthcarePanelBaa:
      "Wir unterzeichnen mit Kunden im Gesundheitswesen ein Business Associate Agreement (BAA), bevor PHI weitergegeben werden.",
    exploreHealthcare: "Gesundheitswesen entdecken",
    seeCaseStudies: "Fallstudien ansehen",
    servicesPanelEyebrow: "Was wir tun",
    servicesPanelTitle: "KI-Agenten und individuelle Software",
    servicesPanelBody:
      "Agenten, Automatisierung sowie Web-, Mobile- und Cloud-Produkte nach Healthcare-Standards.",
    exploreServices: "Services entdecken",
    company: "Unternehmen",
    companyPanelEyebrow: "Projekt starten",
    companyPanelTitle: "Sie haben ein Projekt im Kopf?",
    companyPanelBody:
      "Teilen Sie uns Ihre Ziele und Ihren Zeitplan mit – wir planen mit Ihnen den nächsten Schritt.",
    socialsTitle: "Sie finden uns auf",
  },
  pages: {
    services: {
      our: "Unsere",
      title: "Services",
      subtitle:
        "Wir entwickeln End-to-End-Digitallösungen – von beeindruckenden Frontends bis zu intelligenten KI-Backends. Entdecke unsere Kompetenzen.",
      learnMore: "Mehr erfahren",
    },
    solutions: {
      our: "Unsere",
      title: "Lösungen",
      subtitle:
        "Wir entwickeln End-to-End-Digitallösungen – von beeindruckenden Frontends bis zu intelligenten KI-Backends. Entdecke unsere Kompetenzen.",
      learnMore: "Mehr erfahren",
    },
    relatedInsights: {
      eyebrow: "Weiterführende Artikel (auf Englisch)",
      title: "Weiterlesen",
      allInsights: "Alle Artikel (auf Englisch)",
    },
    relatedLinks: {
      eyebrow: "Weiter entdecken",
      title: "Verwandte Seiten",
      specialtiesEyebrow: "Gesundheitswesen",
      specialtiesTitle: "Software nach Fachrichtung",
      siblingsEyebrow: "Mehr aus diesem Bereich",
      overview: "Überblick",
    },
  },
  contact: {
    kicker: "KONTAKT",
    title: "Kontakt aufnehmen",
    bookMeeting: "Termin über Calendly buchen",
    supportEmail: "Support-E-Mail",
    callAnytime: "24/7 erreichbar",
    liveChat: "Live-Chat",
    whatsappContact: "Auf WhatsApp chatten",
    successTitle: "Nachricht gesendet!",
    successSubtitle:
      "Danke für Ihre Nachricht. Wir melden uns so schnell wie möglich bei Ihnen.",
    privacyNote:
      "*Wir geben Ihre personenbezogenen Daten nicht an Dritte weiter.",
    sending: "WIRD GESENDET...",
    sendMessage: "NACHRICHT SENDEN",
    errors: { required: "Bitte füllen Sie alle Pflichtfelder aus." },
    form: {
      nameLabel: "NAME*",
      namePlaceholder: "Ihr Name",
      phoneLabel: "TELEFONNUMMER (OPTIONAL)",
      phonePlaceholder: "+49 …",
      emailLabel: "E-MAIL*",
      emailPlaceholder: "name@beispiel.de",
      serviceLabel: "SERVICE AUSWÄHLEN*",
      servicePlaceholder: "Bitte wählen Sie einen Service",
      budgetLabel: "PROJEKTBUDGET*",
      budgetPlaceholder: "Budget auswählen",
      messageLabel: "NACHRICHT*",
      messagePlaceholder: "Ihre Nachricht…",
    },
  },
  footer: {
    languageLabel: "Sprache",
    emailTitle: "E‑Mail",
    callTitle: "Telefon",
    officeLocationTitle: "Standort",
    infoInquiryNote: "(Für allgemeine Anfragen)",
    hiringNote: "(Für Bewerbungen)",
    hiringSalesNote: "(Für Recruiting/Sales)",
    generalEmailLabel: "E-Mail senden",
    careersEmailLabel: "Karriere",
    // securityPoliciesTitle: "Security- & Compliance-Richtlinien",
    // pdfDocumentLabel: "PDF-Dokument",
    privacyPolicy: "Datenschutz",
    collaborations: "Kooperationen",
    reviews: "Kundenbewertungen",
    termsOfUse: "Nutzungsbedingungen",
    protectFromFraud: "Betrugsschutz",
    accessibility: "Barrierefreiheit",
    backToTop: "Nach oben",
    ctaLine1: "Wachsen Sie mit Woltrio.",
    ctaLine2: "Starten Sie noch heute.",
    menuTitle: "Menü",
    connectTitle: "Vernetzen",
    areasServedTitle: "Einsatzgebiete",
    askAiAbout: "Fragen Sie die KI nach",
    headquartersLabel: "Hauptsitz",
    isoScopeLabel: "Nach ISO/IEC 27001:2022 zertifiziertes Entwicklungszentrum",
    allRightsReserved: "Alle Rechte vorbehalten.",
  },
  inquiry: {
    stepLabel: "von",
    step1: {
      title: "Wählen Sie Ihren Weg zu einem beeindruckenden Erlebnis!",
      subtitle:
        "Beginnen Sie Ihre Reise mit uns – teilen Sie Ihre Bedürfnisse und wir gestalten gemeinsam die perfekte Lösung!",
    },
    step2: {
      title: "Erzählen Sie uns, wovon Sie träumen",
      subtitle:
        "Teilen Sie Ihre Vision mit uns und wir verwandeln Ihren Traum in eine wunderschön gestaltete Realität!",
      placeholder: "Geben Sie hier Ihre Nachricht ein …",
    },
    step3: {
      title:
        "Haben Sie Unterlagen? Teilen Sie sie, um Ihre Vision zum Leben zu erwecken!",
      subtitle:
        "Laden Sie Ihre Entwürfe hoch, damit wir die perfekte Designlösung entwickeln können!",
      uploadClick: "Zum Hochladen klicken",
      uploadDrag: "oder per Drag & Drop",
      uploadHint: "PDF, DOC, TXT oder ZIP (MAX. 5 MB)",
    },
    step4: {
      title:
        "Es ist Zeit, den Funken zu entzünden und Ihre Ideen Wirklichkeit werden zu lassen!",
      subtitle:
        "Teilen Sie Ihre Kontaktdaten und wir melden uns, um Ihre Vision zu besprechen!",
      fullName: "Vollständiger Name",
      email: "E‑Mail‑Adresse",
      phone: "Telefonnummer (Optional)",
    },
    services: {
      design: "Design",
      development: "Entwicklung",
      cloud: "Cloud & Hosting",
      ai: "KI-Lösungen",
    },
    errors: {
      services: "Bitte wählen Sie mindestens einen Service aus, um fortzufahren.",
      vision: "Bitte beschreiben Sie Ihre Vision.",
      name: "Vollständiger Name ist erforderlich.",
      emailRequired: "E‑Mail‑Adresse ist erforderlich.",
      emailInvalid: "Bitte geben Sie eine gültige E‑Mail‑Adresse ein.",
      phone: "Bitte geben Sie eine gültige Telefonnummer ein (mindestens 10 Ziffern).",
      generic: "Etwas ist schiefgelaufen. Bitte versuchen Sie es erneut.",
    },
    footer: {
      nextToEnter: "Enter zum Fortfahren",
      back: "ZURÜCK",
      next: "WEITER",
      skip: "ÜBERSPRINGEN",
      submit: "ABSENDEN",
      submitting: "WIRD GESENDET",
    },
    success: {
      title: "Anfrage erhalten!",
      thanks: "Danke",
      message:
        "Wir haben Ihre Angaben erhalten. Ein Mitglied unseres Teams antwortet Ihnen noch am selben Werktag.",
      backToHome: "Zurück zur Startseite",
    },
  },
  responsePromise: {
    heading: "So geht es weiter",
    steps: [
      {
        title: "Antwort am selben Werktag",
        detail: "Ein Mitglied unseres Teams liest Ihre Nachricht und antwortet noch am selben Werktag.",
      },
      {
        title: "NDA innerhalb von 24 Stunden",
        detail:
          "Auf Wunsch unterzeichnen wir vor dem Austausch von Details Ihre oder unsere Geheimhaltungsvereinbarung innerhalb von 24 Stunden. Bei Healthcare-Projekten schließen wir zudem einen BAA ab, bevor PHI geteilt werden.",
      },
      {
        title: "Kostenloser Scope innerhalb von 3 Werktagen",
        detail: "Nach einem ersten Gespräch erhalten Sie innerhalb von 3 Werktagen einen kostenlosen Scope mit Festpreis pro Meilenstein. Keine Vorauszahlung: Jeder Meilenstein wird nach Ihrer Freigabe bezahlt.",
      },
    ],
    line: "Wir antworten am selben Werktag, unterzeichnen ein NDA innerhalb von 24 Stunden und senden innerhalb von 3 Werktagen einen kostenlosen Scope. Keine Vorauszahlung.",
  },
};

export function getMessages(locale: Locale): Messages {
  if (locale === "de-de") return de;
  if (locale === "fr-fr") {
    return {
      common: {
        viewProject: "Voir le projet",
        processTitle: "Notre",
        processBoldTitle: "processus",
        ctaGetStarted: "DÉMARRER",
        loading: "Chargement",
      },
      home: {
        metaTitle: "Woltrio | Agence logicielle IA",
        metaDescription:
        "Woltrio est une agence logicielle IA spécialisée en développement web, applications mobiles et solutions d’intelligence artificielle. Transformez votre entreprise avec nous.",
        heroHeadline: "La version actuelle",
        heroSubheadline:
          "Systèmes EMR/EHR sécurisés et conformes HIPAA, automatisation par IA et plateformes numériques évolutives pour les acteurs de la santé aux États‑Unis.",
        heroCtaText: "PARLONS‑EN",
        heroDescription:
          "Woltrio fournit une technologie de santé conforme HIPAA qui transforme les soins aux patients, améliore les résultats et réduit la complexité opérationnelle. De la conception initiale au déploiement à grande échelle, nous construisons des systèmes auxquels les équipes de santé font confiance.",
        heroCtaText2: "Voir nos services",
      },
      allDone: {
        headline: "De la vision à la réalité",
        subheadline:
          "Chez Woltrio, nous concevons des expériences digitales qui stimulent la croissance, l’engagement et l’innovation de votre entreprise.",
        services: [
          {
            title: "Conception produit & UI/UX",
            description:
              "Nous créons des solutions numériques centrées utilisateur pour la santé. Des portails patients intuitifs aux tableaux de bord pour les soignants, notre démarche de design privilégie l’ergonomie, l’accessibilité et la conformité. Nous transformons des parcours cliniques complexes en expériences simples, engageantes et efficaces.",
          },
          {
            title: "Développement web & mobile HealthTech",
            description:
              "Des systèmes de santé sécurisés, évolutifs et conformes. Notre équipe construit des plateformes EMR/EHR conformes HIPAA, des applications de télésanté et des solutions mobiles. Avec des technologies web, mobiles et cloud modernes, nous livrons des produits fiables qui améliorent la prise en charge et les workflows cliniques.",
          },
        ],
      },
      homePage: {
        sectionLabels: {
          services: "Services",
          whatWeDo: "Ce que nous faisons",
          projects: "Projets",
          faq: "FAQ",
        },
        hero: {
          ofWorld: "du",
          needsUpdate: "a besoin d’une mise à jour",
        },
        movingCardsCta: "VOIR TOUT",
        reviewsLink: "Lire tous les avis clients",
        clutchBadgesNote: "Chaque badge renvoie au classement Clutch où figure Woltrio (vérifié le {date}). Noté {rating} sur la base de {count} avis Clutch vérifiés.",
        faq: {
          titleLine1: "Questions",
          titleLine2: "fréquemment posées",
          subtitle:
            "Besoin d’informations de base ? Notre FAQ vous donne des réponses claires aux questions les plus fréquentes.",
        },
        serviceFan: {
          "1": "Frontend",
          "2": "Développement API",
          "3": "Design UI/UX",
          "4": "Gestion BDD",
          "5": "MVP",
          "7": "Analytics",
          "8": "DevOps",
        },
        movingCards: {
          "1": {
            title: "Logiciels HealthTech innovants & solutions EMR/EHR",
            description:
              "Chez Woltrio, nous construisons des plateformes de santé sécurisées et conformes (orientation HIPAA) pour les acteurs américains. Des solutions EMR/EHR aux workflows cliniques optimisés, nos produits aident les équipes à travailler plus efficacement, protéger les données patients et améliorer les résultats.",
          },
          "2": {
            title: "Développement web & mobile centré utilisateur",
            description:
              "Nous donnons vie aux designs grâce à un développement scalable et soigné, pensé pour engager les utilisateurs et convertir.",
          },
          "3": {
            title: "Intégration & interopérabilité des systèmes de santé",
            description:
              "La santé moderne dépend de systèmes connectés. Nous facilitons une communication fluide entre plateformes EMR/EHR, laboratoires, pharmacies, facturation et applications tierces — avec des échanges de données sécurisés, précis et en temps réel.",
          },
        },
        accordion: {
          "1": {
            title: "Solutions IA & automatisation",
            description:
              "Nos solutions d’automatisation par IA transforment vos processus, améliorent la précision et maximisent la productivité.",
          },
          "2": {
            title: "UI/UX & design produit digital",
            description:
              "Des expériences pixel‑perfect pour le web et le mobile, qui captivent et renforcent l’engagement.",
          },
          "3": {
            title: "Développement web & mobile full‑stack",
            description:
              "Des solutions robustes et évolutives, basées sur des architectures modernes, conçues pour la performance.",
          },
          "4": {
            title: "Développement API & intégration système",
            description:
              "Connexion fluide de votre écosystème logiciel via des APIs sécurisées, performantes et des intégrations fiables.",
          },
        },
        featuredProjects: {
          "vine-university-lms": {
            title: "Vine – LMS universitaire",
            description:
              "Une plateforme complète pour étudiants, enseignants et universités afin de simplifier les workflows pédagogiques.",
          },
          "marketintent-data-automation": {
            title: "MarketIntent – automatisation des données",
            description:
              "Une plateforme d’automatisation pour le scraping et la gestion de données, avec scripts sur mesure et spider crawling.",
          },
          "trendsetgo-ai-trends-analyzer": {
            title: "TrendSetGo – analyseur de tendances IA",
            description:
              "Un analyseur de tendances intelligent exploitant des données Apollo pour fournir des insights actionnables.",
          },
        },
        whyChoose: {
          title: "Pourquoi les organisations de santé choisissent Woltrio",
          description:
            "Woltrio est une entreprise de technologie de santé spécialisée dans le développement de logiciels personnalisés pour les prestataires de soins, les systèmes de santé et les entreprises de santé numérique. Nous combinons une connaissance approfondie du secteur de la santé avec l'ingénierie logicielle moderne pour créer des solutions qui améliorent les résultats pour les patients et l'efficacité opérationnelle.",
          description2:
            "Notre équipe comprend les flux de travail de santé, les exigences de conformité et l'importance critique de la sécurité des données. Nous ne construisons pas seulement des logiciels - nous construisons des systèmes de santé que vos équipes utiliseront et aimeront réellement.",
          features: [
            "Expertise en EMR, EHR et développement d'applications de santé",
            "Spécialistes de l'intégration HL7, FHIR et de la santé",
            "Développement HIPAA-first avec sécurité by design",
            "Antécédents prouvés avec des entreprises de santé",
            "Méthodologie agile avec des équipes dédiées à la santé",
          ],
        },
        benefits: {
          title: "Pourquoi choisir Woltrio",
          subtitle:
            "Nous ne sommes pas seulement des développeurs - nous sommes des partenaires technologiques de santé engagés dans le succès de votre organisation.",
          items: [
            {
              title: "Sécurité d'abord",
              description:
                "Architecture de sécurité de niveau entreprise avec chiffrement, authentification et pistes d'audit intégrés dans chaque système dès le premier jour.",
            },
            {
              title: "Architecture évolutive",
              description:
                "Systèmes cloud-native conçus pour évoluer de la startup à l'entreprise, gérant des millions de dossiers patients et de transactions.",
            },
            {
              title: "Expertise en santé",
              description:
                "Compréhension approfondie des flux de travail de santé, des exigences de conformité et des opérations cliniques. Nous parlons santé.",
            },
            {
              title: "Équipes dédiées",
              description:
                "Équipes de développement dédiées concentrées exclusivement sur votre projet, apportant continuité et connaissance approfondie du produit.",
            },
            {
              title: "Design centré utilisateur",
              description:
                "Experts UX de santé qui comprennent les flux de travail des cliniciens, réduisant la charge et améliorant les taux d'adoption.",
            },
            {
              title: "Conformité et certification",
              description:
                "HIPAA, RGPD et conformité spécifique à la santé intégrés au développement, aux tests et au déploiement, avec des systèmes conçus pour accompagner votre audit SOC 2.",
            },
          ],
        },
        process: {
          title: "Notre processus de développement",
          subtitle:
            "Une méthodologie éprouvée pour fournir des logiciels de santé qui répondent à vos besoins, délais et exigences de conformité.",
          steps: [
            {
              title: "Découverte et planification",
              description:
                "Plongée approfondie dans vos flux de travail, exigences et écosystème de santé. Définition de la portée, du calendrier et des métriques de succès.",
            },
            {
              title: "Stratégie et architecture",
              description:
                "Conception d'une architecture sécurisée et évolutive. Planification des intégrations, des mesures de conformité et de la sélection de la pile technologique.",
            },
            {
              title: "Design et prototypage",
              description:
                "Création de flux utilisateur, wireframes et prototypes haute fidélité. Validation avec les cliniciens et les parties prenantes.",
            },
            {
              title: "Développement et intégration",
              description:
                "Sprints agiles avec tests continus, revues de sécurité et intégration avec vos systèmes de santé existants.",
            },
            {
              title: "Lancement et optimisation",
              description:
                "Déploiement, formation des utilisateurs, optimisation continue et support 24/7 pour assurer un fonctionnement fluide et l'adoption par les utilisateurs.",
            },
          ],
        },
        industries: {
          title: "Industries que nous servons",
          subtitle:
            "Nous avons une expertise approfondie dans l'ensemble de l'écosystème de la santé, des grands systèmes hospitaliers aux startups healthtech émergentes.",
          items: [
            "Hôpitaux et systèmes de santé",
            "Cliniques et soins d'urgence",
            "Startups HealthTech",
            "Formation médicale",
            "Entreprises EdTech",
            "Plateformes SaaS de santé",
            "Fabricants de dispositifs médicaux",
            "Entreprises pharmaceutiques",
          ],
        },
      },
      workPage: {
        metaTitle: "Portfolio & études de cas",
        metaDescription:
          "Découvrez le portfolio et les études de cas de Woltrio : conception, développement et livraison de produits numériques de qualité pour des équipes en croissance.",
        hero: {
          title: "Projets livrés",
          boldTitle: "à nos clients",
          subtitle:
            "Études de cas en santé, fintech, edtech et IA, avec les résultats obtenus pour chaque client.",
        },
        intro: {
          title: "Des produits concrets, conçus pour grandir",
          paragraphs: [
            "Chaque projet ici a commencé comme toutes nos collaborations : une conversation de découverte sur le flux de travail à corriger, les utilisateurs concernés et les contraintes — conformité, calendrier, systèmes existants — à respecter. Nos équipes conçoivent, développent et livrent ensuite des logiciels prêts pour la production, pas des prototypes qui s'arrêtent après la démo.",
            "Ces réalisations couvrent des plateformes de santé conformes HIPAA et HL7/FHIR, des outils d'automatisation basés sur l'IA, des produits SaaS et des systèmes de gestion de l'apprentissage, chacun suivi par une équipe dédiée de l'architecture jusqu'au lancement et au-delà. Découvrez un projet ci-dessous ou contactez-nous.",
          ],
        },
        cta: {
          title: "Un design remarquable, prêt quand vous en avez besoin",
          subtitle: "à un tarif accessible pour aider votre entreprise à grandir.",
          buttonText: "NOUS CONTACTER",
        },
        learnMore: "En savoir plus",
        categories: {
          ALL: "TOUT",
          SAAS: "SaaS",
          WEBSITES: "Sites web",
          "WEB APPS": "Apps web",
          AI: "IA",
          "MOBILE APPS": "Apps mobiles",
          BRANDING: "Branding",
          "UX DISCOVERY": "UX discovery",
          MVP: "MVP",
        },
        notFound: {
          metaTitle: "Projet introuvable",
          metaDescription:
            "Le projet demandé est introuvable. Contactez Woltrio si vous avez besoin d’aide ou souhaitez discuter d’un projet similaire.",
          title: "Projet introuvable",
          back: "Retour aux projets",
        },
        details: {
          backToAllProjects: "Retour à tous les projets",
          viewLiveProject: "Voir le projet en ligne",
          caseStudySuffix: "Étude de cas",
          defaultMetaDescription:
            "Découvrez cette étude de cas et voyez comment Woltrio conçoit, développe et livre des produits numériques fiables pour répondre à de vrais défis business.",
          industryLabel: "Secteur",
          headquartersLabel: "Siège social",
          platformLabel: "Plateforme",
          challengeTitle: "Le défi business que nous avons résolu",
          solutionTitle: "La solution technique que nous avons livrée",
          impactTitle: "Impact du projet et résultats mesurables",
          impact: {
            efficiencyIncrease: "Gain d’efficacité",
            userEngagement: "Engagement utilisateur",
            activeUsers: "Utilisateurs actifs",
          },
          galleryItemAlt: "Élément de galerie",
          ctaTitle: "Parlez-nous de votre projet",
          ctaDescription:
            "Vous avez un défi similaire ? Discutons de ce que vous construisez et de la façon dont notre équipe peut vous aider à le livrer.",
        },
        projects: {
          "vine-university-lms": {
            title: "Vine – LMS universitaire",
            description:
              "Une plateforme complète pour étudiants, enseignants et universités afin de simplifier les workflows pédagogiques.",
            fullDescription:
              "Vine est un Learning Management System (LMS) moderne conçu pour les universités. Il relie l’administration, les enseignants et les étudiants via une plateforme unifiée de gestion des cours, des notes et de la communication.",
            challenge:
              "Les universités utilisent souvent des systèmes fragmentés : données étudiants, contenus et notes sont répartis sur plusieurs outils. Résultat : inefficacités administratives et expérience utilisateur dégradée.",
            solution:
              "Nous avons développé une plateforme centralisée et cloud‑native avec Next.js et AdonisJS. Vine intègre des notifications en temps réel, des workflows d’évaluation automatisés et un portail étudiant responsive, réduisant l’administratif de 40%.",
          },
          "marketintent-data-automation": {
            title: "MarketIntent – automatisation des données",
            description:
              "Une plateforme d’automatisation pour le scraping et la gestion de données, avec scripts sur mesure et spider crawling.",
            fullDescription:
              "MarketIntent automatise l’extraction de données de marché et de concurrence pour fournir des insights en temps réel sur les tendances et la tarification.",
            challenge:
              "La collecte manuelle est lente et sujette aux erreurs. Les équipes avaient besoin d’un moyen rapide et fiable de traiter de grands volumes sans mobiliser trop de ressources humaines.",
            solution:
              "Nous avons construit un moteur de scraping scalable avec Puppeteer et FastAPI. PM2 orchestre des milliers de jobs concurrents, avec nettoyage et structuration automatique des données.",
          },
          "trendsetgo-ai-trends-analyzer": {
            title: "TrendSetGo – analyseur de tendances IA",
            description:
              "Un analyseur de tendances intelligent exploitant des données Apollo pour fournir des insights actionnables.",
            fullDescription:
              "TrendSetGo analyse des données sociales et marché via des algorithmes IA pour détecter les tendances avant qu’elles ne deviennent mainstream. Pensé pour les équipes marketing et produit.",
            challenge:
              "Détecter tôt les tendances est difficile à cause du bruit sur les réseaux. Les outils traditionnels sont souvent réactifs plutôt que prédictifs.",
            solution:
              "En combinant des données Apollo avec des modèles IA (FastAPI), TrendSetGo filtre le bruit et attribue un score prédictif pour agir plusieurs semaines avant les concurrents.",
          },
          "daisy-ai-intelligent-emr": {
            title: "Daisy.ai – EMR intelligent",
            description:
              "Un EMR (dossier médical électronique) professionnel intégré à une plateforme IA sur mesure pour gagner en efficacité.",
            fullDescription:
              "Daisy.ai réinvente l’EMR en intégrant une assistance IA directement dans le workflow clinique : documentation, suggestions et suivis patients.",
            challenge:
              "Les médecins passent souvent près de 50% de leur temps sur la paperasse. Les EMR existants sont lourds et peu intuitifs, ce qui accentue la charge et le burn‑out.",
            solution:
              "Nous avons créé une interface EMR épurée et pilotable à la voix. Le backend IA génère automatiquement des notes cliniques (avec consentement), à valider puis signer.",
          },
          "billingvista---billing-solutions": {
            title: "BillingVista – plateforme de facturation",
            description:
              "Une homepage moderne et une UI pour une plateforme complète de facturation et d’abonnements.",
            fullDescription:
              "BillingVista simplifie la facturation complexe et la gestion d’abonnements pour les entreprises SaaS, avec un dashboard clair pour les factures et paiements.",
            challenge:
              "La logique de facturation (prorata, usage) produit des factures difficiles à comprendre et augmente le churn. Il fallait rendre la complexité lisible.",
            solution:
              "Nous avons travaillé l’UX/UI pour clarifier les cycles et visualiser l’usage et les coûts en temps réel, renforçant transparence et confiance.",
          },
          "aelapro-job-notifications": {
            title: "AelaPro – alertes emploi",
            description:
              "Un service de notifications intelligent qui connecte les candidats à des opportunités en temps réel.",
            fullDescription:
              "AelaPro surveille les job boards 24/7 et alerte instantanément les chercheurs d’emploi dès qu’un poste correspond à leurs critères, offrant un avantage de rapidité.",
            challenge:
              "Dans un marché compétitif, le timing est crucial. Rafraîchir manuellement les annonces est inefficace et fait manquer des opportunités.",
            solution:
              "Nous avons construit un moteur d’agrégation temps réel et des alertes e‑mail/SMS en quelques secondes, basé sur des filtres précis.",
          },
        },
      },
      nav: {
        services: "Services",
        solutions: "Solutions",
        work: "Études de cas",
        insights: "Insights",
        team: "Équipe",
        careers: "Carrières",
        about: "À propos",
        contact: "Contact",
        inquiryNow: "DÉMARRER UN PROJET",
        viewAllServices: "VOIR TOUS LES SERVICES",
        viewAllSolutions: "VOIR TOUTES LES SOLUTIONS",
        servicesDropdownHint:
          "Vous voulez construire un produit numérique ? Parlons‑en !",
        solutionsDropdownHint:
          "Découvrez nos solutions end‑to‑end pour votre entreprise.",
        healthcare: "Santé",
        healthcarePanelEyebrow: "Ingénierie logicielle pour la santé",
        healthcarePanelTitle: "Conçu pour les soins réglementés",
        healthcarePanelIso: "Centre de développement certifié ISO/IEC 27001:2022",
        healthcarePanelBaa:
          "Nous signons un Business Associate Agreement (BAA) avec nos clients du secteur de la santé avant tout partage de PHI.",
        exploreHealthcare: "Découvrir la santé",
        seeCaseStudies: "Voir les études de cas",
        servicesPanelEyebrow: "Ce que nous faisons",
        servicesPanelTitle: "Agents IA et logiciels sur mesure",
        servicesPanelBody:
          "Agents, automatisation et produits web, mobiles et cloud, aux standards de la santé.",
        exploreServices: "Découvrir nos services",
        company: "Entreprise",
        companyPanelEyebrow: "Démarrer un projet",
        companyPanelTitle: "Vous avez un projet en tête ?",
        companyPanelBody:
          "Partagez vos objectifs et votre calendrier, nous planifierons la prochaine étape avec vous.",
        socialsTitle: "Retrouvez-nous sur",
      },
      pages: {
        services: {
          our: "Nos",
          title: "Services",
          subtitle:
            "Nous construisons des solutions numériques de bout en bout : des front‑ends soignés aux backends IA intelligents. Découvrez nos expertises.",
          learnMore: "En savoir plus",
        },
        solutions: {
          our: "Nos",
          title: "Solutions",
          subtitle:
            "Nous construisons des solutions numériques de bout en bout : des front‑ends soignés aux backends IA intelligents. Découvrez nos expertises.",
          learnMore: "En savoir plus",
        },
        relatedInsights: {
          eyebrow: "Articles associés (en anglais)",
          title: "Pour aller plus loin",
          allInsights: "Tous les articles (en anglais)",
        },
        relatedLinks: {
          eyebrow: "Continuer la visite",
          title: "Pages associées",
          specialtiesEyebrow: "Santé",
          specialtiesTitle: "Logiciels par spécialité",
          siblingsEyebrow: "Plus dans ce service",
          overview: "Vue d'ensemble",
        },
      },
      contact: {
        kicker: "CONTACT",
        title: "Nous contacter",
        bookMeeting: "Réserver un rendez‑vous sur Calendly",
        supportEmail: "E‑mail support",
        callAnytime: "Appel 24/7",
        liveChat: "Chat en direct",
        whatsappContact: "Discuter sur WhatsApp",
        successTitle: "Message envoyé !",
        successSubtitle:
          "Merci de nous avoir contactés. Nous vous répondrons dès que possible.",
        privacyNote:
          "*Nous nous engageons à ne pas divulguer vos informations personnelles à des tiers.",
        sending: "ENVOI…",
        sendMessage: "ENVOYER LE MESSAGE",
        errors: { required: "Veuillez remplir tous les champs obligatoires." },
        form: {
          nameLabel: "NOM*",
          namePlaceholder: "Votre nom",
          phoneLabel: "TÉLÉPHONE (OPTIONNEL)",
          phonePlaceholder: "+33 …",
          emailLabel: "E‑MAIL*",
          emailPlaceholder: "vous@exemple.com",
          serviceLabel: "SERVICE*",
          servicePlaceholder: "Sélectionnez un service",
          budgetLabel: "BUDGET*",
          budgetPlaceholder: "Choisissez votre budget",
          messageLabel: "MESSAGE*",
          messagePlaceholder: "Votre message…",
        },
      },
      footer: {
        languageLabel: "Langue",
        emailTitle: "E‑mail",
        callTitle: "Téléphone",
        officeLocationTitle: "Adresse",
        infoInquiryNote: "(Infos générales)",
        hiringNote: "(Recrutement)",
        hiringSalesNote: "(Recrutement/Ventes)",
        generalEmailLabel: "Nous écrire",
        careersEmailLabel: "Carrières",
        // securityPoliciesTitle: "Politiques de sécurité & conformité",
        // pdfDocumentLabel: "Document PDF",
        privacyPolicy: "Confidentialité",
        collaborations: "Partenariats",
        reviews: "Avis clients",
        termsOfUse: "Conditions d’utilisation",
        protectFromFraud: "Protection contre la fraude",
        accessibility: "Accessibilité",
        backToTop: "Retour en haut",
        ctaLine1: "Grandissez avec Woltrio.",
        ctaLine2: "Commencez votre aventure dès aujourd’hui.",
        menuTitle: "Menu",
        connectTitle: "Réseaux",
        areasServedTitle: "Zones desservies",
        askAiAbout: "Poser une question à l’IA sur",
        headquartersLabel: "Siège",
        isoScopeLabel: "Centre de développement certifié ISO/IEC 27001:2022",
        allRightsReserved: "Tous droits réservés.",
      },
      inquiry: {
        stepLabel: "sur",
        step1: {
          title: "Choisissez votre voie vers une expérience exceptionnelle !",
          subtitle:
            "Commencez votre aventure avec nous, partagez vos besoins et nous façonnerons ensemble la solution parfaite !",
        },
        step2: {
          title: "Dites‑nous ce dont vous rêvez",
          subtitle:
            "Partagez votre vision avec nous et nous transformerons votre rêve en une réalité magnifiquement conçue !",
          placeholder: "Saisissez votre message ici…",
        },
        step3: {
          title:
            "Vous avez des documents ? Partagez‑les pour donner vie à votre vision !",
          subtitle:
            "Téléversez vos ébauches pour nous aider à concevoir la solution idéale !",
          uploadClick: "Cliquez pour téléverser",
          uploadDrag: "ou glissez‑déposez",
          uploadHint: "PDF, DOC, TXT ou ZIP (MAX. 5 Mo)",
        },
        step4: {
          title:
            "Il est temps d’allumer l’étincelle pour transformer vos idées en réalité !",
          subtitle:
            "Partagez vos coordonnées et nous vous recontacterons pour discuter de votre vision !",
          fullName: "Nom complet",
          email: "Adresse e‑mail",
          phone: "Numéro de téléphone (facultatif)",
        },
        services: {
          design: "Design",
          development: "Développement",
          cloud: "Cloud & Hébergement",
          ai: "Solutions IA",
        },
        errors: {
          services: "Veuillez sélectionner au moins un service pour continuer.",
          vision: "Veuillez décrire votre vision.",
          name: "Le nom complet est requis.",
          emailRequired: "L’adresse e‑mail est requise.",
          emailInvalid: "Veuillez saisir une adresse e‑mail valide.",
          phone: "Veuillez entrer un numéro de téléphone valide (au moins 10 chiffres).",
          generic: "Une erreur est survenue. Veuillez réessayer.",
        },
        footer: {
          nextToEnter: "Entrée pour continuer",
          back: "RETOUR",
          next: "SUIVANT",
          skip: "PASSER",
          submit: "ENVOYER",
          submitting: "ENVOI EN COURS",
        },
        success: {
          title: "Demande reçue !",
          thanks: "Merci",
          message:
            "Nous avons bien reçu vos informations. Un membre de notre équipe vous répondra le jour ouvré même.",
          backToHome: "Retour à l’accueil",
        },
      },
      responsePromise: {
        heading: "La suite",
        steps: [
          {
            title: "Une réponse le jour ouvré même",
            detail: "Un membre de notre équipe lit votre message et vous répond le jour ouvré même.",
          },
          {
            title: "NDA sous 24 heures",
            detail:
              "Si vous le souhaitez avant de partager des détails, nous signons votre accord de confidentialité, ou le nôtre, sous 24 heures. Pour les projets de santé, nous signons aussi un BAA avant tout partage de données de santé.",
          },
          {
            title: "Un cadrage gratuit sous 3 jours ouvrés",
            detail: "Après un premier échange, vous recevez sous 3 jours ouvrés un cadrage gratuit avec un prix fixe par jalon. Rien à payer d'avance : chaque jalon est payé après votre validation.",
          },
        ],
        line: "Nous répondons le jour ouvré même, signons un NDA sous 24 heures et envoyons un cadrage gratuit sous 3 jours ouvrés. Rien à payer d'avance.",
      },
    };
  }
  return en;
}

